import { applyDamageTypeTraits } from '../damage/damageTypeTraits.mjs';
import { applyIncomingDamageModifiers } from '../damage/incomingDamage.mjs';
import { hasActiveEffect } from '../combat/activeEffects.mjs';
import { getClassLevel } from '../classIdentity.mjs';

export const normalSaveDamage = (rawDamage, succeeded, normalOutcome) => {
  const rule = succeeded ? normalOutcome?.successDamageRule : normalOutcome?.failureDamageRule;
  if (rule === 'full') return Math.max(0, Math.floor(rawDamage));
  if (rule === 'half') return Math.floor(Math.max(0, Math.floor(rawDamage)) / 2);
  if (rule === 'none' || rule === 'zero') return 0;
  throw new Error(`Unsupported save damage rule: ${rule || 'missing'}`);
};

// Evasion: Rogue 7 and Monk 7 (2024) share the rule; the record names whichever class grants it.
export const evaluateEvasion = (context, { character, activeEffects = [] } = {}) => {
  const featureId = getClassLevel(character, 'rogue') >= 7 ? 'rogue:evasion' : getClassLevel(character, 'monk') >= 7 ? 'monk:evasion' : null;
  if (!featureId || context?.delivery !== 'saving-throw' ||
      context?.save?.ability !== 'dexterity' || context?.save?.normalOutcome?.successDamageRule !== 'half' ||
      hasActiveEffect(activeEffects, context?.targetCombatantId, 'condition:incapacitated')) return [];
  return [context.save.succeeded
    ? { featureId, operation: 'replace-with-zero', basis: 'save-outcome-override' }
    : { featureId, operation: 'halve-floor', basis: 'raw-damage' }];
};

export const resolveSavingThrowDamage = (context, queries = {}) => {
  const rawDamage = Math.max(0, Math.floor(context?.damage?.rawTotal || 0));
  const components = context?.damage?.components || [];
  const hasComponentTotals = components.length && components.every((component) => Number.isFinite(component.rolledTotal));
  const normalDamage = hasComponentTotals ? components.reduce((sum, component) => sum + normalSaveDamage(component.rolledTotal, context?.save?.succeeded, context?.save?.normalOutcome), 0)
    : normalSaveDamage(rawDamage, context?.save?.succeeded, context?.save?.normalOutcome);
  const modifiers = evaluateEvasion(context, queries);
  const arithmetic = applyIncomingDamageModifiers({ rawDamage, calculatedDamage: normalDamage }, modifiers);
  const componentOutcomes = hasComponentTotals ? components.map((component) => {
    const normal = normalSaveDamage(component.rolledTotal, context?.save?.succeeded, context?.save?.normalOutcome);
    const evasion = modifiers[0]?.operation;
    const beforeTraits = evasion === 'replace-with-zero' ? 0 : evasion === 'halve-floor' ? Math.floor(component.rolledTotal / 2) : normal;
    return { rolledTotal: component.rolledTotal, normalDamage: normal, ...applyDamageTypeTraits({ type: component.type, damage: beforeTraits }, queries.character, { combatState: { activeEffects: queries.activeEffects }, targetCombatantId: context.targetCombatantId }) };
  }) : [];
  const beforeTraits = componentOutcomes.reduce((sum, component) => sum + component.before, 0);
  const finalDamage = hasComponentTotals ? componentOutcomes.reduce((sum, component) => sum + component.finalDamage, 0) : arithmetic.finalDamage;
  const modifierRecords = arithmetic.modifiers.map((record) => ({ ...record, after: hasComponentTotals ? beforeTraits : record.after,
    reduction: record.before - (hasComponentTotals ? beforeTraits : record.after) }));
  return Object.freeze({
    ...context, damage: Object.freeze({ ...context.damage, rawTotal: rawDamage }),
    damageOutcome: Object.freeze({ rawDamage, normalDamage, modifierRecords, componentOutcomes, finalDamage }),
    conditionOutcome: context?.conditionOutcome ?? null,
  });
};

/** Every die at its highest (Overchannel), in the order damage evidence uses. */
export const maximizedDamageDice = (components) => components.map(({ formula }) => {
  const [, count, sides] = /^(\d+)d(\d+)$/.exec(formula || '') || [];
  return Array(Number(count) || 0).fill(Number(sides));
});

export const validateDiceEvidence = (formula, values) => {
  const match = /^(\d+)d(\d+)$/.exec(formula || '');
  if (!match || !Array.isArray(values) || values.length !== Number(match[1]) ||
      values.some((value) => !Number.isInteger(value) || value < 1 || value > Number(match[2]))) {
    throw new Error('Damage roll evidence does not match the canonical formula.');
  }
  return values.reduce((sum, value) => sum + value, 0);
};

